import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  GraduationCap,
  GripVertical,
  Home,
  Menu,
  BarChart3,
  Search,
  Settings,
  Star,
  Trophy,
  Users,
  X,
  Globe,
} from "lucide-react";
import { getIconForApp } from "../hub/hubIcons.js";
import { AuthAvatarFace } from "../shell/AuthAvatarFace.jsx";
import { ShellUserMenuPanel } from "../shell/ShellUserMenuPanel.jsx";
import {
  isAppVisibleInLauncher,
  resolveLauncherAllowedAppKeysSet,
} from "./useAppLauncherState.jsx";
import {
  PLATFORM_SIDE_NAV_COLLAPSED_KEY,
  PLATFORM_SIDE_NAV_GROUPS,
  PLATFORM_SIDE_NAV_MOBILE_MQ,
  collectPlatformSideNavSearchHits,
  filterPlatformSideNavChildren,
  resolveActiveSideNavIds,
  resolvePlatformSideNavHref,
} from "./platformSideNavConfig.js";
import {
  bumpSideNavUsage,
  dismissSideNavAutoShortcutId,
  pinSideNavShortcutId,
  readSideNavDismissedAutoIds,
  readSideNavPinnedShortcutIds,
  readSideNavShortcutUsage,
  reorderSideNavPinnedShortcutIds,
  resolveSideNavShortcutRows,
  unpinSideNavShortcutId,
  writeSideNavDismissedAutoIds,
  writeSideNavPinnedShortcutIds,
} from "./platformSideNavShortcuts.js";

const SIDE_NAV_DND_MIME = "application/x-tm-side-nav-id";

function read(t, key, fallback = "") {
  const v = typeof t === "function" ? t(key) : key;
  return (v && v !== key ? v : fallback || key || "").toString();
}

function TennisBallIcon({ size = 18 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M4.2 7.2 C 7.6 10.2, 7.6 13.8, 4.2 16.8" />
      <path d="M19.8 7.2 C 16.4 10.2, 16.4 13.8, 19.8 16.8" />
    </svg>
  );
}

const GROUP_ICONS = {
  home: Home,
  calendar: CalendarDays,
  menschen: Users,
  training: GraduationCap,
  tournaments: Trophy,
  club: Building2,
  insights: BarChart3,
  einstellungen: Settings,
  tenniswelt: Globe,
};

function groupIcon(group) {
  if (GROUP_ICONS[group.id]) return GROUP_ICONS[group.id];
  if (group.appKey) return getIconForApp(group.appKey) || Home;
  return Home;
}

function childIcon(child) {
  const key = String(child?.appKey || "").trim();
  if (!key) return null;
  return getIconForApp(key);
}

function readCollapsedPref() {
  try {
    return window.localStorage.getItem(PLATFORM_SIDE_NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsedPref(collapsed) {
  try {
    window.localStorage.setItem(
      PLATFORM_SIDE_NAV_COLLAPSED_KEY,
      collapsed ? "1" : "0",
    );
  } catch {
    /* ignore */
  }
}

function useIsMobileNav() {
  const [mobile, setMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.matchMedia?.(PLATFORM_SIDE_NAV_MOBILE_MQ)?.matches ?? false;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let mq = null;
    try {
      mq = window.matchMedia?.(PLATFORM_SIDE_NAV_MOBILE_MQ) || null;
    } catch {
      return undefined;
    }
    if (!mq) return undefined;
    const onChange = () => setMobile(!!mq.matches);
    onChange();
    try {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    } catch {
      mq.addListener?.(onChange);
      return () => mq.removeListener?.(onChange);
    }
  }, []);
  return mobile;
}

function itemVisible(item, allowedSet, accessReady, opts = {}) {
  if (item?.id === "settings-apps" && opts.canAccessVereinsabo === false) {
    return false;
  }
  const required = String(item?.requiredFeature || "").trim();
  if (required && !opts.bypassSubscriptionFeatures) {
    const feats = opts.subscriptionFeatures;
    if (feats && typeof feats === "object" && !feats[required]) {
      return false;
    }
  }
  const appKey = item?.appKey;
  if (appKey == null || appKey === "") return true;
  return isAppVisibleInLauncher(appKey, {
    accessReady,
    allowedAppKeys: allowedSet,
  });
}

/**
 * @param {{
 *   t: Function,
 *   launcherAccess?: { accessReady?: boolean, allowedAppKeys?: Set<string>|null },
 *   shellAccessRestricted?: boolean,
 *   mobileOpen?: boolean,
 *   onMobileOpenChange?: (open: boolean) => void,
 *   onGoHome?: () => void,
 *   authUserAvatar?: string,
 *   authUserInitials?: string,
 *   authUserLabel?: string,
 *   authUserEmail?: string,
 *   onSignOut?: () => void,
 *   canAccessAccessRequestsAdmin?: boolean,
 *   canAccessVereinsabo?: boolean,
 *   subscriptionFeatures?: {
 *     billing?: boolean,
 *     analytics?: boolean,
 *     clubApps?: boolean,
 *     clubInsights?: boolean,
 *   }|null,
 *   bypassSubscriptionFeatures?: boolean,
 *   coachDeskSelfProfileHref?: string,
 *   homeMode?: boolean,
 *   userClubs?: Array<{ id: string, label?: string }>|null,
 * }} props
 */
export function PlatformSideNav({
  t,
  launcherAccess = null,
  shellAccessRestricted = false,
  mobileOpen = false,
  onMobileOpenChange,
  onGoHome,
  authUserAvatar = "",
  authUserInitials = "",
  authUserLabel = "",
  authUserEmail = "",
  onSignOut,
  canAccessAccessRequestsAdmin = false,
  canAccessVereinsabo = false,
  subscriptionFeatures = null,
  bypassSubscriptionFeatures = false,
  coachDeskSelfProfileHref = "",
  homeMode = false,
  userClubs = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobileNav();
  const flyoutId = useId();
  const [collapsed, setCollapsed] = useState(() =>
    typeof window === "undefined" ? false : readCollapsedPref(),
  );
  const [openGroupId, setOpenGroupId] = useState("");
  const [flyoutGroupId, setFlyoutGroupId] = useState("");
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [pinnedShortcutIds, setPinnedShortcutIds] = useState(() =>
    typeof window === "undefined" ? [] : readSideNavPinnedShortcutIds(),
  );
  const [shortcutUsage, setShortcutUsage] = useState(() =>
    typeof window === "undefined" ? {} : readSideNavShortcutUsage(),
  );
  const [dismissedAutoIds, setDismissedAutoIds] = useState(() =>
    typeof window === "undefined" ? [] : readSideNavDismissedAutoIds(),
  );
  const [shortcutDropActive, setShortcutDropActive] = useState(false);
  const navRef = useRef(null);
  const flyoutRef = useRef(null);
  const flyoutTriggerRef = useRef(null);
  const userMenuDetailsRef = useRef(null);
  const searchInputRef = useRef(null);
  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    } catch {
      return false;
    }
  }, []);

  const accessReady =
    launcherAccess == null ? true : launcherAccess.accessReady !== false;
  const allowedSet = useMemo(
    () => resolveLauncherAllowedAppKeysSet(launcherAccess?.allowedAppKeys),
    [launcherAccess?.allowedAppKeys],
  );

  const featureOpts = useMemo(
    () => ({
      canAccessVereinsabo,
      subscriptionFeatures,
      bypassSubscriptionFeatures,
    }),
    [canAccessVereinsabo, subscriptionFeatures, bypassSubscriptionFeatures],
  );

  const groups = useMemo(() => {
    if (shellAccessRestricted) {
      return PLATFORM_SIDE_NAV_GROUPS.filter((g) => g.id === "home");
    }
    return PLATFORM_SIDE_NAV_GROUPS.map((group) => {
      if (!Array.isArray(group.children) || !group.children.length) {
        if (!itemVisible(group, allowedSet, accessReady, featureOpts))
          return null;
        return group;
      }
      const children = filterPlatformSideNavChildren(group.children, (c) =>
        itemVisible(c, allowedSet, accessReady, featureOpts),
      );
      if (!children.length) return null;
      return { ...group, children };
    }).filter(Boolean);
  }, [allowedSet, accessReady, shellAccessRestricted, featureOpts]);

  const active = useMemo(
    () =>
      resolveActiveSideNavIds(
        location.pathname,
        PLATFORM_SIDE_NAV_GROUPS,
        location.search,
      ),
    [location.pathname, location.search],
  );

  useEffect(() => {
    if (active.groupId) setOpenGroupId(active.groupId);
  }, [active.groupId]);

  useEffect(() => {
    if (isMobile) onMobileOpenChange?.(false);
  }, [location.pathname, isMobile, onMobileOpenChange]);

  useEffect(() => {
    const el = userMenuDetailsRef.current;
    if (el && typeof el.removeAttribute === "function") {
      el.removeAttribute("open");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onPointerDown = (e) => {
      const el = userMenuDetailsRef.current;
      if (!el || !el.hasAttribute("open")) return;
      if (el.contains(e.target)) return;
      el.removeAttribute("open");
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const el = userMenuDetailsRef.current;
      if (el && el.hasAttribute("open")) el.removeAttribute("open");
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!isMobile || !mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, mobileOpen]);

  const setCollapsedAndPersist = useCallback((next) => {
    setCollapsed(next);
    writeCollapsedPref(next);
    setFlyoutGroupId("");
    if (next) setSearchQuery("");
  }, []);

  const closeFlyout = useCallback(() => {
    setFlyoutGroupId("");
    const btn = flyoutTriggerRef.current;
    if (btn && typeof btn.focus === "function") {
      try {
        btn.focus();
      } catch {
        /* ignore */
      }
    }
    flyoutTriggerRef.current = null;
  }, []);

  useEffect(() => {
    if (!flyoutGroupId) return undefined;
    const onPointer = (e) => {
      const nav = navRef.current;
      const fly = flyoutRef.current;
      if (fly?.contains(e.target)) return;
      if (nav?.contains(e.target)) return;
      closeFlyout();
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeFlyout();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [flyoutGroupId, closeFlyout]);

  const go = useCallback(
    (href, navId = "") => {
      if (!href) return;
      const id = String(navId || "").trim();
      if (id) {
        setShortcutUsage((prev) => bumpSideNavUsage(id, prev));
      }
      setFlyoutGroupId("");
      setSearchQuery("");
      if (isMobile) onMobileOpenChange?.(false);
      if (href === "/hub" && typeof onGoHome === "function") {
        onGoHome();
        return;
      }
      const resolved = resolvePlatformSideNavHref(href);
      // Explizit pathname+search+hash setzen, damit ein Ziel ohne Query
      // (z. B. /trainingsdesk/kunden) hängengebliebene ?person= entfernt.
      try {
        const u = new URL(resolved, window.location.origin);
        navigate({
          pathname: u.pathname,
          search: u.search,
          hash: u.hash,
        });
      } catch {
        navigate(resolved);
      }
    },
    [isMobile, navigate, onGoHome, onMobileOpenChange],
  );

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    try {
      searchInputRef.current?.focus?.();
    } catch {
      /* ignore */
    }
  }, []);

  const persistPinned = useCallback((next) => {
    const saved = writeSideNavPinnedShortcutIds(next);
    setPinnedShortcutIds(saved);
    return saved;
  }, []);

  const persistDismissed = useCallback((next) => {
    const saved = writeSideNavDismissedAutoIds(next);
    setDismissedAutoIds(saved);
    return saved;
  }, []);

  const pinShortcut = useCallback(
    (id, index) => {
      const key = String(id || "").trim();
      if (!key) return;
      persistPinned(pinSideNavShortcutId(pinnedShortcutIds, key, index));
      persistDismissed(dismissedAutoIds.filter((x) => x !== key));
    },
    [dismissedAutoIds, persistDismissed, persistPinned, pinnedShortcutIds],
  );

  const removeShortcut = useCallback(
    (row) => {
      const key = String(row?.id || "").trim();
      if (!key) return;
      if (row?.source === "pin") {
        persistPinned(unpinSideNavShortcutId(pinnedShortcutIds, key));
      } else {
        persistDismissed(dismissSideNavAutoShortcutId(dismissedAutoIds, key));
      }
    },
    [dismissedAutoIds, persistDismissed, persistPinned, pinnedShortcutIds],
  );

  const onNavItemDragStart = useCallback((e, id) => {
    const key = String(id || "").trim();
    if (!key || !e?.dataTransfer) return;
    e.dataTransfer.setData(SIDE_NAV_DND_MIME, key);
    e.dataTransfer.setData("text/plain", key);
    e.dataTransfer.effectAllowed = "copyMove";
  }, []);

  const onShortcutsDragOver = useCallback((e) => {
    e.preventDefault();
    try {
      e.dataTransfer.dropEffect = "copy";
    } catch {
      /* ignore */
    }
    setShortcutDropActive(true);
  }, []);

  const onShortcutsDragLeave = useCallback((e) => {
    const related = e.relatedTarget;
    if (related && e.currentTarget?.contains?.(related)) return;
    setShortcutDropActive(false);
  }, []);

  const onShortcutsDrop = useCallback(
    (e) => {
      e.preventDefault();
      setShortcutDropActive(false);
      const id =
        e.dataTransfer?.getData(SIDE_NAV_DND_MIME) ||
        e.dataTransfer?.getData("text/plain") ||
        "";
      pinShortcut(id);
    },
    [pinShortcut],
  );

  const openFlyoutFor = useCallback((groupId, buttonEl) => {
    if (!buttonEl) return;
    const rect = buttonEl.getBoundingClientRect();
    const top = Math.max(8, Math.min(rect.top, window.innerHeight - 280));
    setFlyoutPos({ top, left: rect.right + 8 });
    flyoutTriggerRef.current = buttonEl;
    setFlyoutGroupId(groupId);
  }, []);

  const flyoutGroup = groups.find((g) => g.id === flyoutGroupId) || null;
  const desktopCollapsed = collapsed && !isMobile;
  const showExpandedLabels = isMobile || !collapsed;

  const searchHits = useMemo(() => {
    if (!showExpandedLabels) return [];
    const q = String(searchQuery || "").trim();
    if (!q) return [];
    return collectPlatformSideNavSearchHits(groups, { t, query: q });
  }, [groups, searchQuery, showExpandedLabels, t]);

  const searchActive =
    showExpandedLabels && String(searchQuery || "").trim().length > 0;

  const shortcutRows = useMemo(() => {
    if (!showExpandedLabels || searchActive) return [];
    return resolveSideNavShortcutRows({
      groups,
      t,
      pinned: pinnedShortcutIds,
      usage: shortcutUsage,
      dismissed: dismissedAutoIds,
    });
  }, [
    dismissedAutoIds,
    groups,
    pinnedShortcutIds,
    searchActive,
    shortcutUsage,
    showExpandedLabels,
    t,
  ]);

  const pinnedIdSet = useMemo(
    () => new Set(pinnedShortcutIds.map((x) => String(x || "").trim())),
    [pinnedShortcutIds],
  );

  const renderPinButton = (id) => {
    const key = String(id || "").trim();
    if (!key) return null;
    const isPinned = pinnedIdSet.has(key);
    return (
      <button
        type="button"
        className={`platformSideNav__pinBtn${isPinned ? " is-pinned" : ""}`}
        aria-label={
          isPinned
            ? read(t, "nav.side.shortcuts.unpin")
            : read(t, "nav.side.shortcuts.pin")
        }
        title={
          isPinned
            ? read(t, "nav.side.shortcuts.unpin")
            : read(t, "nav.side.shortcuts.pin")
        }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isPinned) {
            persistPinned(unpinSideNavShortcutId(pinnedShortcutIds, key));
          } else {
            pinShortcut(key);
          }
        }}
      >
        <Star
          size={14}
          strokeWidth={2.25}
          fill={isPinned ? "currentColor" : "none"}
          aria-hidden
        />
      </button>
    );
  };

  const renderChildLink = (child, opts = {}) => {
    if (String(child?.kind || "") === "section") {
      return (
        <li
          key={child.id}
          className="platformSideNav__section"
          role="presentation"
        >
          <span className="platformSideNav__sectionLabel">
            {read(t, child.labelKey)}
          </span>
        </li>
      );
    }
    const isActive = active.itemId === child.id;
    const Icon = childIcon(child);
    return (
      <li key={child.id}>
        <div
          className={`platformSideNav__itemWrap${isActive ? " is-active" : ""}`}
        >
          <button
            type="button"
            className={`platformSideNav__item platformSideNav__item--child${isActive ? " is-active" : ""}`}
            aria-current={isActive ? "page" : undefined}
            draggable={showExpandedLabels}
            onDragStart={(e) => onNavItemDragStart(e, child.id)}
            onClick={() => go(child.href, child.id)}
          >
            {opts.showIcon && Icon ? (
              <Icon
                size={16}
                strokeWidth={2}
                aria-hidden
                className="platformSideNav__itemIcon"
              />
            ) : null}
            <span className="platformSideNav__itemLabel">
              {read(t, child.labelKey)}
            </span>
          </button>
          {showExpandedLabels ? renderPinButton(child.id) : null}
        </div>
      </li>
    );
  };

  const navInner = (
    <>
      <div className="platformSideNav__brand">
        <button
          type="button"
          className="platformSideNav__brandBtn"
          onClick={() => go("/hub")}
          aria-label={read(t, "nav.home")}
          title={read(t, "appTopbar.platformMark")}
        >
          <span className="platformSideNav__brandBadge" aria-hidden>
            <TennisBallIcon size={18} />
          </span>
          {showExpandedLabels ? (
            <span className="platformSideNav__brandText">
              {read(t, "appTopbar.platformMark")}
            </span>
          ) : null}
        </button>
        {!isMobile ? (
          <button
            type="button"
            className="platformSideNav__collapseBtn"
            aria-label={
              collapsed
                ? read(t, "nav.side.expand")
                : read(t, "nav.side.collapse")
            }
            title={
              collapsed
                ? read(t, "nav.side.expand")
                : read(t, "nav.side.collapse")
            }
            onClick={() => setCollapsedAndPersist(!collapsed)}
          >
            {collapsed ? (
              <ChevronsRight size={20} strokeWidth={2.25} aria-hidden />
            ) : (
              <ChevronsLeft size={20} strokeWidth={2.25} aria-hidden />
            )}
          </button>
        ) : (
          <button
            type="button"
            className="platformSideNav__collapseBtn"
            aria-label={read(t, "common.close")}
            onClick={() => onMobileOpenChange?.(false)}
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
        )}
      </div>

      {showExpandedLabels ? (
        <div className="platformSideNav__search">
          <label className="sr-only" htmlFor={`${flyoutId}-side-search`}>
            {read(t, "nav.side.search.aria")}
          </label>
          <div className="platformSideNav__searchField">
            <Search
              size={16}
              strokeWidth={2}
              aria-hidden
              className="platformSideNav__searchIcon"
            />
            <input
              ref={searchInputRef}
              id={`${flyoutId}-side-search`}
              type="search"
              className="platformSideNav__searchInput"
              value={searchQuery}
              placeholder={read(t, "nav.side.search.placeholder")}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setSearchQuery(String(e.target?.value || ""))}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  clearSearch();
                }
              }}
            />
            {searchQuery ? (
              <button
                type="button"
                className="platformSideNav__searchClear"
                aria-label={read(t, "nav.side.search.clear")}
                onClick={clearSearch}
              >
                <X size={14} strokeWidth={2.25} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {showExpandedLabels && !searchActive ? (
        <div
          className={`platformSideNav__shortcuts${shortcutDropActive ? " is-dropTarget" : ""}`}
          onDragOver={onShortcutsDragOver}
          onDragLeave={onShortcutsDragLeave}
          onDrop={onShortcutsDrop}
        >
          <div className="platformSideNav__shortcutsHead">
            <span className="platformSideNav__shortcutsTitle">
              {read(t, "nav.side.shortcuts.title")}
            </span>
          </div>
          {shortcutRows.length === 0 ? (
            <p className="platformSideNav__shortcutsEmpty">
              {shortcutDropActive
                ? read(t, "nav.side.shortcuts.dropHint")
                : read(t, "nav.side.shortcuts.emptyHint")}
            </p>
          ) : (
            <ul className="platformSideNav__list platformSideNav__list--shortcuts">
              {shortcutRows.map((row) => {
                const isActive = active.itemId
                  ? active.itemId === row.id
                  : active.groupId === row.id;
                const Icon = row.appKey
                  ? getIconForApp(row.appKey) || Home
                  : GROUP_ICONS[row.id] || Home;
                return (
                  <li key={`sc-${row.source}-${row.id}`}>
                    <div
                      className={`platformSideNav__itemWrap platformSideNav__itemWrap--shortcut${isActive ? " is-active" : ""}`}
                      draggable={row.source === "pin"}
                      onDragStart={(e) => {
                        if (row.source !== "pin") return;
                        onNavItemDragStart(e, row.id);
                      }}
                      onDragOver={(e) => {
                        if (row.source !== "pin") return;
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        if (row.source !== "pin") return;
                        e.preventDefault();
                        e.stopPropagation();
                        const fromId =
                          e.dataTransfer?.getData(SIDE_NAV_DND_MIME) ||
                          e.dataTransfer?.getData("text/plain") ||
                          "";
                        if (!fromId || fromId === row.id) {
                          pinShortcut(fromId);
                          return;
                        }
                        if (pinnedIdSet.has(fromId)) {
                          persistPinned(
                            reorderSideNavPinnedShortcutIds(
                              pinnedShortcutIds,
                              fromId,
                              row.id,
                            ),
                          );
                        } else {
                          const toIdx = pinnedShortcutIds.indexOf(row.id);
                          pinShortcut(fromId, toIdx < 0 ? undefined : toIdx);
                        }
                      }}
                    >
                      {row.source === "pin" ? (
                        <span
                          className="platformSideNav__shortcutHandle"
                          aria-hidden
                        >
                          <GripVertical size={14} strokeWidth={2} />
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className={`platformSideNav__item platformSideNav__item--shortcut${isActive ? " is-active" : ""}`}
                        aria-current={isActive ? "page" : undefined}
                        onClick={() => go(row.href, row.id)}
                      >
                        <Icon
                          size={18}
                          strokeWidth={2}
                          aria-hidden
                          className="platformSideNav__itemIcon"
                        />
                        <span className="platformSideNav__searchHitText">
                          <span className="platformSideNav__itemLabel">
                            {row.label}
                          </span>
                          {row.source === "auto" ? (
                            <span className="platformSideNav__shortcutAutoBadge">
                              {read(t, "nav.side.shortcuts.autoBadge")}
                            </span>
                          ) : row.groupLabel ? (
                            <span className="platformSideNav__searchHitGroup">
                              {row.groupLabel}
                            </span>
                          ) : null}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="platformSideNav__shortcutRemove"
                        aria-label={read(t, "nav.side.shortcuts.unpin")}
                        title={read(t, "nav.side.shortcuts.unpin")}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeShortcut(row);
                        }}
                      >
                        <X size={14} strokeWidth={2.25} aria-hidden />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}

      <nav
        className="platformSideNav__nav"
        aria-label={read(t, "nav.side.aria")}
      >
        {searchActive ? (
          <ul className="platformSideNav__list platformSideNav__list--search">
            {searchHits.length === 0 ? (
              <li className="platformSideNav__searchEmpty" role="status">
                {read(t, "nav.side.search.empty")}
              </li>
            ) : (
              searchHits.map((hit) => {
                const isActive = active.itemId
                  ? active.itemId === hit.id
                  : active.groupId === hit.id;
                const Icon = hit.appKey
                  ? getIconForApp(hit.appKey) || Home
                  : GROUP_ICONS[hit.id] || Home;
                return (
                  <li key={`search-${hit.id}`}>
                    <div
                      className={`platformSideNav__itemWrap${isActive ? " is-active" : ""}`}
                    >
                      <button
                        type="button"
                        className={`platformSideNav__item platformSideNav__item--searchHit${isActive ? " is-active" : ""}`}
                        aria-current={isActive ? "page" : undefined}
                        draggable
                        onDragStart={(e) => onNavItemDragStart(e, hit.id)}
                        onClick={() => go(hit.href, hit.id)}
                      >
                        <Icon
                          size={18}
                          strokeWidth={2}
                          aria-hidden
                          className="platformSideNav__itemIcon"
                        />
                        <span className="platformSideNav__searchHitText">
                          <span className="platformSideNav__itemLabel">
                            {hit.label}
                          </span>
                          {hit.groupLabel ? (
                            <span className="platformSideNav__searchHitGroup">
                              {hit.groupLabel}
                            </span>
                          ) : null}
                        </span>
                      </button>
                      {renderPinButton(hit.id)}
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        ) : (
          <ul className="platformSideNav__list">
            {groups.map((group) => {
              const hasChildren =
                Array.isArray(group.children) && group.children.length > 0;
              const Icon = groupIcon(group);
              const groupActive = active.groupId === group.id;
              const expanded =
                !desktopCollapsed && openGroupId === group.id && hasChildren;

              if (!hasChildren) {
                return (
                  <li key={group.id}>
                    <div
                      className={`platformSideNav__itemWrap${groupActive ? " is-active" : ""}`}
                    >
                      <button
                        type="button"
                        className={`platformSideNav__item${groupActive ? " is-active" : ""}`}
                        aria-current={groupActive ? "page" : undefined}
                        title={
                          desktopCollapsed ? read(t, group.labelKey) : undefined
                        }
                        draggable={showExpandedLabels}
                        onDragStart={(e) => onNavItemDragStart(e, group.id)}
                        onClick={() => go(group.href, group.id)}
                      >
                        <Icon
                          size={20}
                          strokeWidth={2}
                          aria-hidden
                          className="platformSideNav__itemIcon"
                        />
                        {showExpandedLabels ? (
                          <span className="platformSideNav__itemLabel">
                            {read(t, group.labelKey)}
                          </span>
                        ) : (
                          <span className="sr-only">
                            {read(t, group.labelKey)}
                          </span>
                        )}
                      </button>
                      {showExpandedLabels ? renderPinButton(group.id) : null}
                    </div>
                  </li>
                );
              }

              return (
                <li key={group.id}>
                  <button
                    type="button"
                    className={`platformSideNav__item platformSideNav__item--group${groupActive ? " is-activeGroup" : ""}`}
                    aria-expanded={
                      desktopCollapsed ? flyoutGroupId === group.id : expanded
                    }
                    aria-controls={
                      desktopCollapsed && flyoutGroupId === group.id
                        ? flyoutId
                        : undefined
                    }
                    title={
                      desktopCollapsed ? read(t, group.labelKey) : undefined
                    }
                    onClick={(e) => {
                      const groupHref = String(group.href || "").trim();
                      if (desktopCollapsed) {
                        if (groupHref) go(groupHref, group.id);
                        if (flyoutGroupId === group.id) closeFlyout();
                        else openFlyoutFor(group.id, e.currentTarget);
                        return;
                      }
                      if (groupHref) {
                        // z. B. Einstellungen: Untermenü öffnen und Seite rechts zeigen
                        setOpenGroupId(group.id);
                        setFlyoutGroupId("");
                        go(groupHref, group.id);
                        return;
                      }
                      setOpenGroupId((prev) =>
                        prev === group.id ? "" : group.id,
                      );
                      setFlyoutGroupId("");
                    }}
                  >
                    <Icon
                      size={20}
                      strokeWidth={2}
                      aria-hidden
                      className="platformSideNav__itemIcon"
                    />
                    {showExpandedLabels ? (
                      <>
                        <span className="platformSideNav__itemLabel">
                          {read(t, group.labelKey)}
                        </span>
                        {expanded ? (
                          <ChevronDown
                            size={16}
                            className="platformSideNav__chevron"
                            aria-hidden
                          />
                        ) : (
                          <ChevronRight
                            size={16}
                            className="platformSideNav__chevron"
                            aria-hidden
                          />
                        )}
                      </>
                    ) : (
                      <span className="sr-only">{read(t, group.labelKey)}</span>
                    )}
                  </button>
                  {expanded ? (
                    <ul className="platformSideNav__sublist">
                      {group.children
                        .filter((c) => c?.navTier !== "explore")
                        .map((child) => renderChildLink(child))}
                      {group.children.some((c) => c?.navTier === "explore") ? (
                        <li className="platformSideNav__tierLabel" aria-hidden>
                          {read(t, "nav.side.tier.explore")}
                        </li>
                      ) : null}
                      {group.children
                        .filter((c) => c?.navTier === "explore")
                        .map((child) => renderChildLink(child))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      <div className="platformSideNav__footer">
        <details
          ref={userMenuDetailsRef}
          className="platformSideNav__userMenu app-topbar__menu"
        >
          <summary
            className="platformSideNav__item platformSideNav__item--profile platformSideNav__userMenuTrigger"
            title={desktopCollapsed ? read(t, "nav.profile") : undefined}
            aria-label={read(t, "nav.menu")}
          >
            <span className="platformSideNav__avatar" aria-hidden>
              <AuthAvatarFace
                avatarUrl={authUserAvatar}
                initials={authUserInitials}
              />
            </span>
            {showExpandedLabels ? (
              <span className="platformSideNav__itemLabel">
                {read(t, "nav.profile")}
              </span>
            ) : (
              <span className="sr-only">{read(t, "nav.profile")}</span>
            )}
          </summary>
          <ShellUserMenuPanel
            t={t}
            authUserLabel={authUserLabel}
            authUserEmail={authUserEmail}
            onSignOut={onSignOut}
            canAccessAccessRequestsAdmin={canAccessAccessRequestsAdmin}
            canAccessVereinsabo={canAccessVereinsabo}
            shellAccessRestricted={shellAccessRestricted}
            coachDeskSelfProfileHref={coachDeskSelfProfileHref}
            homeMode={homeMode}
            userClubs={userClubs}
            launcherAccess={launcherAccess}
            showLegalLinks={isMobile}
          />
        </details>
      </div>
    </>
  );

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  const overlays =
    portalTarget &&
    createPortal(
      <>
        {isMobile && mobileOpen ? (
          <button
            type="button"
            className="platformSideNav__backdrop"
            aria-label={read(t, "common.close")}
            onClick={() => onMobileOpenChange?.(false)}
          />
        ) : null}
        {desktopCollapsed && flyoutGroup ? (
          <div
            id={flyoutId}
            ref={flyoutRef}
            className="platformSideNav__flyout"
            role="dialog"
            aria-label={read(t, flyoutGroup.labelKey)}
            style={{ top: flyoutPos.top, left: flyoutPos.left }}
          >
            <p className="platformSideNav__flyoutTitle">
              {read(t, flyoutGroup.labelKey)}
            </p>
            <ul className="platformSideNav__flyoutList">
              {flyoutGroup.children.map((child) => {
                if (String(child?.kind || "") === "section") {
                  return (
                    <li
                      key={child.id}
                      className="platformSideNav__flyoutSection"
                      role="presentation"
                    >
                      <span className="platformSideNav__flyoutSectionLabel">
                        {read(t, child.labelKey)}
                      </span>
                    </li>
                  );
                }
                const isActive = active.itemId === child.id;
                return (
                  <li key={child.id}>
                    <button
                      type="button"
                      className={`platformSideNav__flyoutItem${isActive ? " is-active" : ""}`}
                      aria-current={isActive ? "page" : undefined}
                      onClick={() => go(child.href, child.id)}
                    >
                      {read(t, child.labelKey)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </>,
      portalTarget,
    );

  return (
    <>
      <aside
        ref={navRef}
        className={[
          "platformSideNav",
          desktopCollapsed ? "is-collapsed" : "is-expanded",
          isMobile ? "is-mobile" : "is-desktop",
          isMobile && mobileOpen ? "is-open" : "",
          reduceMotion ? "is-reducedMotion" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-side-nav
        aria-hidden={isMobile && !mobileOpen ? true : undefined}
      >
        {navInner}
      </aside>
      {overlays}
    </>
  );
}

export function PlatformSideNavMobileToggle({ t, onClick, open = false }) {
  return (
    <button
      type="button"
      className="platformSideNav__mobileToggle"
      aria-label={read(t, "nav.menu")}
      aria-expanded={open}
      onClick={onClick}
    >
      <Menu size={20} strokeWidth={2} aria-hidden />
    </button>
  );
}
